"use client";

// Root-level error boundary. Catches errors thrown in the root layout itself,
// where `app/error.tsx` cannot reach. It REPLACES the root layout, so it must
// render its own <html>/<body> and gets no Providers/Footer/fonts — we import
// globals.css so the Tailwind utility classes (e.g. bg-primary) resolve, and
// fall back to the system font for this rare catastrophic case. Do NOT use
// router-dependent components (e.g. PrimaryButton) here — reset() is enough.

import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/nextjs";
import "./globals.css";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // The global boundary replaces the whole document, so this <h1> is the page's
  // only heading — move focus to it on mount so AT users get oriented.
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    Sentry.captureException(error);
    headingRef.current?.focus();
  }, [error]);

  return (
    <html lang="en">
      <body className="antialiased">
        <div className="min-h-screen flex flex-col items-center justify-center text-center gap-4 px-6">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-base font-semibold text-gray-900 outline-none"
          >
            Something went wrong
          </h1>
          <p className="max-w-xs text-sm text-gray-600">
            An unexpected error occurred. Please try again.
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-2 rounded-[30px] bg-primary px-6 py-3 font-semibold text-white"
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
